import { useOutletContext } from 'react-router-dom';
import type { SeasonContext } from '../lib/seasonContext.ts';
import { PotHeader } from '../components/PotHeader.tsx';
import { LedgerRow } from '../components/LedgerRow.tsx';
import { money as fmtMoney } from '../lib/format.ts';
import { LEAGUE } from '@ff/core';

/**
 * The Book: the flow of the money itself — who is up, who is down, and what
 * each manager settles at the end of the year.
 *
 * The side-pot tickets moved to the standings page, next to the table they are
 * won from. What stays here is the one question this page exists to answer:
 * what do I owe, and to whom.
 */
export function MoneyBoard() {
  const { stored, nameOf, teamOf, live } = useOutletContext<SeasonContext>();
  const { data, money } = stored.bundle;

  // Rank by net dollars, the whole thesis. Ties break on gross winnings, then
  // alphabetically by name so a no-games-yet board reads sensibly instead of in
  // arbitrary order.
  const ranked = [...money.owners].sort(
    (a, b) =>
      b.net - a.net ||
      b.earned - a.earned ||
      nameOf(a.ownerId).localeCompare(nameOf(b.ownerId)),
  );

  return (
    <div className="space-y-10">
      <PotHeader
        money={money}
        season={data.season}
        leagueName={data.leagueName || LEAGUE.name}
        live={live}
        nameOf={nameOf}
      />

      <section>
        <div className="flex items-baseline justify-between">
          <h2 className="eyebrow">The Settlement</h2>
          <span className="font-mono text-xs text-chalk-dim">
            net of {fmtMoney(money.config.buyIn)} buy-in
          </span>
        </div>
        <div className="mt-2 divide-y divide-felt-line">
          {ranked.map((m, i) => (
            <LedgerRow
              key={m.ownerId}
              rank={i + 1}
              name={nameOf(m.ownerId)}
              team={teamOf(m.ownerId)}
              money={m}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
